import { Component, input, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SafeHtml } from '@angular/platform-browser';
import { HtmlSanitizerService, SanitizationConfig } from '../../../services/html-sanitizer.service';

@Component({
  selector: 'app-html',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './app-html.component.html',
  styleUrl: './app-html.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppHtmlComponent {
  private htmlSanitizer = inject(HtmlSanitizerService);

  // Input signals - modern Angular approach
  content = input<string>('');
  size = input<'sm' | 'base' | 'lg' | 'xl'>('base');
  color = input<'primary' | 'secondary' | 'default' | 'white' | 'black'>('default');
  
  // Security configuration input
  sanitizationConfig = input<Partial<SanitizationConfig>>({});
  
  // Whether to allow only basic text formatting (more restrictive)
  restrictive = input<boolean>(false);

  // Computed signal for sanitized content with proper security
  sanitizedContent = computed(() => {
    const rawContent = this.content();
    
    if (!rawContent) {
      return this.htmlSanitizer.sanitizeHtml('');
    }

    // Use restrictive mode if specified
    if (this.restrictive()) {
      const textContent = this.htmlSanitizer.sanitizeForInput(rawContent);
      return this.htmlSanitizer.sanitizeHtml(textContent);
    }

    // Use configured sanitization
    return this.htmlSanitizer.sanitizeHtml(rawContent, this.sanitizationConfig());
  });

  // Computed signal for container classes - using input signals directly
  containerClasses = computed(() => {
    const classes = [];

    // Size classes
    switch (this.size()) {
      case 'sm':
        classes.push('text-sm');
        break;
      case 'lg':
        classes.push('text-lg');
        break;
      case 'xl':
        classes.push('text-xl');
        break;
      default:
        classes.push('text-base');
    }

    // Color classes
    switch (this.color()) {
      case 'primary':
        classes.push('text-black');
        break;
      case 'secondary':
        classes.push('text-gray-600');
        break;
      case 'white':
        classes.push('text-white');
        break;
      case 'black':
        classes.push('text-black');
        break;
      default:
        classes.push('text-gray-900');
    }

    return classes.join(' ');
  });
}